import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Shield, TrendingUp, Save, Loader2, ToggleRight, ToggleLeft, FileText } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { CollapsibleCard } from '../ui/CollapsibleCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFinancialSection = ({ operation, onRefresh, onEditTariffViaQuote }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transporteurs, setTransporteurs] = useState([]);
  const [form, setForm] = useState({
    estimatedPrice: 0,
    carrierPrice: 0,
    carrierName: '',
    paymentMethod: '',
    paymentStatus: '',
    negotiationEnabled: false,
  });

  useEffect(() => {
    if (operation && !editing) {
      setForm({
        estimatedPrice: operation.estimatedPrice || 0,
        carrierPrice: operation.carrierPrice || 0,
        carrierName: operation.carrierName || operation.partnerName || '',
        paymentMethod: operation.paymentMethod || '',
        paymentStatus: operation.financialStatus || '',
        negotiationEnabled: operation.negotiationEnabled || false,
      });
    }
  }, [operation, editing]);

  useEffect(() => {
    const fetchTransporteurs = async () => {
      try {
        const res = await axios.get(`${API}/transport-partners?limit=200`, {});
        const data = res.data;
        setTransporteurs(data.partners || (Array.isArray(data) ? data : []));
      } catch { /* ignore */ }
    };
    fetchTransporteurs();
  }, []);

  const marge = form.estimatedPrice - form.carrierPrice;
  const margePercent = form.estimatedPrice > 0 ? ((marge / form.estimatedPrice) * 100).toFixed(1) : 0;

  // PM Spec 25/04/2026 §4.4 — Si un devis détaillé existe, le Tarif Client n'est plus éditable inline.
  // Source de vérité = quoteItems → modifier via CreateDetailedQuoteModal pour garantir la cohérence PDF.
  const hasDetailedQuote = Array.isArray(operation?.quoteItems) && operation.quoteItems.length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/transport-requests/${operation.id}`, {
        estimatedPrice: parseFloat(form.estimatedPrice) || 0,
        carrierPrice: parseFloat(form.carrierPrice) || 0,
        carrierName: form.carrierName,
        paymentMethod: form.paymentMethod,
        negotiationEnabled: form.negotiationEnabled,
      }, {});
      toast({ title: "Informations administratives mises à jour" });
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Échec de la mise à jour", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleCard
      id="op-admin-financial"
      title="Informations Administratives"
      icon={Shield}
      defaultOpen={false}
      className="border-amber-200 bg-amber-50/30"
      headerClassName="text-amber-800"
      summaryText={`Marge ${marge.toLocaleString('fr-FR')} XOF · ${form.carrierName || 'Non assigné'}`}
      headerBadge={!editing ? (
        <Button
          data-testid="admin-fin-edit-btn"
          size="sm"
          variant="outline"
          className="h-6 text-[10px] py-0 px-2 ml-1"
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        >
          Modifier
        </Button>
      ) : (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] py-0 px-2" onClick={() => { setEditing(false); setForm({ estimatedPrice: operation.estimatedPrice || 0, carrierPrice: operation.carrierPrice || 0, carrierName: operation.carrierName || operation.partnerName || '', paymentMethod: operation.paymentMethod || '', paymentStatus: operation.financialStatus || '', negotiationEnabled: operation.negotiationEnabled || false }); }}>
            Annuler
          </Button>
          <Button data-testid="admin-fin-save-btn" size="sm" className="h-6 text-[10px] py-0 px-2 bg-amber-600 hover:bg-amber-700" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Sauvegarder
          </Button>
        </div>
      )}
    >
      <div className="space-y-3" data-testid="admin-financial-section">
        {/* Tarifs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Tarif Client</Label>
            {editing && !hasDetailedQuote ? (
              <Input
                data-testid="admin-fin-client-price"
                type="number"
                value={form.estimatedPrice}
                onChange={e => setForm(f => ({ ...f, estimatedPrice: e.target.value }))}
                className="h-8 text-sm mt-1"
              />
            ) : (
              <div className="mt-1 space-y-1">
                <p className="text-lg font-bold text-gray-800">
                  {(form.estimatedPrice || 0).toLocaleString('fr-FR')} <span className="text-xs font-normal text-gray-400">XOF</span>
                </p>
                {editing && hasDetailedQuote && (
                  <div className="space-y-2">
                    <p
                      data-testid="admin-fin-client-price-locked"
                      className="text-[10px] text-gray-500 italic leading-tight"
                    >
                      Devis détaillé actif ({operation.quoteItems.length} ligne{operation.quoteItems.length > 1 ? 's' : ''}). Modifier le Tarif Client via le devis pour garantir la cohérence avec le PDF.
                    </p>
                    {onEditTariffViaQuote && (
                      <Button
                        data-testid="admin-fin-edit-via-quote-btn"
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                        onClick={() => {
                          setEditing(false);
                          onEditTariffViaQuote();
                        }}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Modifier via le devis
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-500">Tarif Transporteur</Label>
            {editing ? (
              <Input
                data-testid="admin-fin-carrier-price"
                type="number"
                value={form.carrierPrice}
                onChange={e => setForm(f => ({ ...f, carrierPrice: e.target.value }))}
                className="h-8 text-sm mt-1"
              />
            ) : (
              <p className="text-lg font-bold text-gray-800 mt-1">
                {(form.carrierPrice || 0).toLocaleString('fr-FR')} <span className="text-xs font-normal text-gray-400">XOF</span>
              </p>
            )}
          </div>
        </div>

        {/* Marge */}
        <Separator />
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-2">
            <TrendingUp className={`w-4 h-4 ${marge >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            <span className="text-xs text-gray-500">Marge</span>
          </div>
          <div data-testid="admin-fin-marge" className="text-right">
            <span className={`text-lg font-bold ${marge >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {marge.toLocaleString('fr-FR')} XOF
            </span>
            <span className="text-xs text-gray-400 ml-2">({margePercent}%)</span>
          </div>
        </div>

        {/* Transporteur */}
        <Separator />
        <div>
          <Label className="text-xs text-gray-500">Transporteur</Label>
          {editing ? (
            <Select value={form.carrierName} onValueChange={v => setForm(f => ({ ...f, carrierName: v }))}>
              <SelectTrigger data-testid="admin-fin-carrier-select" className="h-8 text-sm mt-1">
                <SelectValue placeholder="Sélectionner un transporteur" />
              </SelectTrigger>
              <SelectContent>
                {transporteurs.map(t => (
                  <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>
                ))}
                {form.carrierName && !transporteurs.find(t => t.name === form.carrierName) && (
                  <SelectItem value={form.carrierName}>{form.carrierName} (actuel)</SelectItem>
                )}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm font-medium text-gray-800 mt-1">
              {form.carrierName || <span className="text-gray-400 italic">Non assigné</span>}
            </p>
          )}
        </div>

        {/* Mode de règlement */}
        <div>
          <Label className="text-xs text-gray-500">Mode de règlement</Label>
          {editing ? (
            <Select value={form.paymentMethod} onValueChange={v => setForm(f => ({ ...f, paymentMethod: v }))}>
              <SelectTrigger data-testid="admin-fin-payment-method" className="h-8 text-sm mt-1">
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="virement">Virement</SelectItem>
                <SelectItem value="cheque">Chèque</SelectItem>
                <SelectItem value="espece">Espèce</SelectItem>
                <SelectItem value="mobile_money">Mobile Money</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-gray-800 mt-1 capitalize">
              {form.paymentMethod || <span className="text-gray-400 italic">Non défini</span>}
            </p>
          )}
        </div>

        {/* Toggles */}
        <Separator />
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-gray-500">Négociation client</Label>
            <button
              data-testid="admin-fin-negotiation-toggle"
              onClick={() => {
                if (editing) setForm(f => ({ ...f, negotiationEnabled: !f.negotiationEnabled }));
              }}
              className={`flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full border transition-all ${
                form.negotiationEnabled 
                  ? 'text-emerald-700 bg-emerald-100 border-emerald-300' 
                  : 'text-gray-500 bg-gray-100 border-gray-200'
              } ${editing ? 'cursor-pointer hover:shadow-md active:scale-95' : 'cursor-default opacity-70'}`}
              title={editing ? 'Cliquer pour basculer' : 'Passez en mode édition pour modifier'}
            >
              {form.negotiationEnabled 
                ? <ToggleRight className="w-5 h-5 text-emerald-600" /> 
                : <ToggleLeft className="w-5 h-5 text-gray-400" />
              }
              {form.negotiationEnabled ? 'Activée' : 'Désactivée'}
            </button>
          </div>
          {!editing && (
            <p className="text-[10px] text-gray-400 italic">Cliquez sur "Modifier" pour changer ce paramètre</p>
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default AdminFinancialSection;
